import { supabase } from '../supabaseClient';

// Traxxas Models Forecast -- same workbook as the [57] script
// (57_Traxxas_Model_Stock/57_traxxas_model_stock.py), built in the browser.
// Data: get_traxxas_model_stock() (db_schema/traxxas_model_stock.sql).
// Sell in 6 months / Forcast SOH / SOH Retail Value are live formulas;
// the forecast is straight-line, no seasonality.

const MONTHS          = 12;
const FORECAST_MONTHS = 6;
const FONT            = { name: 'Arial', size: 10 };

const COLUMNS = [
  { header: 'Type',              width: 10 },
  { header: 'Model',             width: 60 },
  { header: 'Code',              width: 13 },
  { header: 'Colours (SOH)',     width: 45 },
  { header: 'SOH',               width: 7 },
  { header: 'Incoming',          width: 9 },
  { header: 'Incoming Colours',  width: 24 },
  { header: `Sold ${MONTHS}m`,   width: 9 },
  { header: 'Sell in 6 months',  width: 16 },
  { header: 'Forcast SOH',       width: 16 },
  { header: 'Retail NZD',        width: 11 },
  { header: 'SOH Retail Value',  width: 16 },
];

const num = (v) => Number(v || 0);

export async function downloadTraxxasModelsForecast() {
  const { data, error } = await supabase.rpc('get_traxxas_model_stock', { p_months: MONTHS });
  if (error) throw error;

  // ~1 MB library -- only fetched when someone actually downloads a report.
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Traxxas Models', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = COLUMNS.map((c) => ({ header: c.header, width: c.width }));

  data.forEach((r, idx) => {
    const i = idx + 2;
    ws.addRow([
      r.model_type,
      r.model_name,
      (r.style_code || '').replace(/^TRX/, ''),
      r.colours_soh,
      num(r.soh),
      num(r.incoming),
      r.incoming_colours,
      num(r.sold),
      { formula: `H${i}/${MONTHS}*${FORECAST_MONTHS}` },
      { formula: `SUM(E${i}:F${i})-I${i}` },
      num(r.retail_nzd),
      { formula: `E${i}*K${i}` },
    ]);
  });

  const last = data.length + 1;
  ws.eachRow((row) => row.eachCell({ includeEmpty: true }, (c) => { c.font = FONT; }));
  ws.getRow(1).eachCell((c) => {
    c.font = { ...FONT, bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E78' } };
  });
  ['I', 'J'].forEach((col) => { ws.getColumn(col).numFmt = '0.0'; });
  ws.getColumn('K').numFmt = '$#,##0.00';
  ws.getColumn('L').numFmt = '$#,##0';
  ws.autoFilter = `A1:L${last}`;
  ws.addConditionalFormatting({
    ref: `J2:J${last}`,
    rules: [{
      type: 'cellIs', operator: 'lessThan', formulae: ['0'],
      style: {
        font: { color: { argb: 'FF9C0006' } },
        fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } },
      },
    }],
  });

  const now = new Date();
  const stamp = now.toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' });
  const note = ws.getCell(`A${last + 2}`);
  note.value = `Source: Supabase get_traxxas_model_stock() — SOH/incoming summed across branches; `
    + `sold = APPROVED sales orders created in the last ${MONTHS} months. `
    + `Sell in 6 months is a straight-line average (no seasonality). Extracted ${stamp}.`;
  note.font = FONT;

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const nzDate = now.toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `Traxxas_Models_${nzDate}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
  return data.length;
}
